/**
 * Шапка кабинета: кнопка меню на телефоне и (KAN-753) индикатор менеджера
 * сессии. KAN-866: слева чей кабинет и кто вошёл (CabinetIdentity), поэтому
 * шапка видна всегда, и на широком экране тоже.
 * KAN-854: менеджеру на широком экране ссылка на приложение для телефона (/m);
 * на телефоне её нет, туда менеджера и так перенаправляет ManagerRouteGuard.
 * KAN-988: ссылка только при функции тарифа manager_app.
 * KAN-925: справа переключатель вошедших аккаунтов.
 * KAN-961: после "Организация / Пользователь" хлебные крошки текущей страницы
 * (TopBarPageTitle), белой шапки у страниц больше нет.
 */
import { Link } from 'react-router-dom';
import { Menu, Smartphone } from 'lucide-react';
import { useCurrentUser, useIsManager } from '@/hooks/useCurrentUser';
import { useFeature } from '@/hooks/useFeature';
import { MOBILE_HOME } from '@/lib/mobileApp';
import { AccountSwitcher } from './AccountSwitcher';
import { CabinetIdentity } from './CabinetIdentity';
import { SessionManagerIndicator } from './SessionManagerIndicator';
import { TopBarPageTitle } from './TopBarPageTitle';
import { usePageHeaderData } from './pageHeaderContext';

interface CabinetTopBarProps {
  onToggleSidebar: () => void;
}

export function CabinetTopBar({ onToggleSidebar }: CabinetTopBarProps) {
  const isManager = useIsManager();
  const hasManagerApp = useFeature('manager_app');
  const { data: user } = useCurrentUser();
  const hasIdentity = Boolean(user?.organization?.name);
  const hasPageTitle = usePageHeaderData() !== null;

  return (
    <div className="flex h-12 items-center gap-2 border-b border-gray-200 bg-white px-4 md:px-6">
      <button
        className="rounded-lg p-2 hover:bg-gray-100 md:hidden"
        onClick={onToggleSidebar}
        aria-label="Открыть меню"
      >
        <Menu className="h-5 w-5" />
      </button>
      {/* KAN-961: кабинет и крошки страницы делят ширину, всё обрезается. */}
      <div className="flex min-w-0 flex-1 items-center gap-2">
        <CabinetIdentity className={hasPageTitle ? 'max-w-[40%] sm:max-w-[45%]' : 'flex-1'} />
        <TopBarPageTitle withLeadingSeparator={hasIdentity} className="flex-1" />
      </div>
      {/* min-w-0: индикатор менеджера обрезается; на телефоне не больше половины шапки. */}
      <div className="ml-auto flex min-w-0 max-w-[50%] items-center justify-end gap-4 md:max-w-none">
        {isManager && hasManagerApp && (
          <Link
            to={MOBILE_HOME}
            className="hidden items-center gap-1 text-sm text-blue-600 hover:underline md:inline-flex"
            data-testid="open-mobile-app-link"
          >
            <Smartphone className="h-4 w-4" aria-hidden="true" />
            Открыть приложение для телефона
          </Link>
        )}
        <SessionManagerIndicator />
      </div>
      <AccountSwitcher variant="cabinet" />
    </div>
  );
}
